import React, { useState, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Chip,
  Divider,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Visibility, Edit, Search, FilterList } from '@mui/icons-material';
import { useDREStore } from '../../stores/dreStore';
import { useDREIntegration } from '../../hooks/useDREIntegration';
import type { DREYear } from '../../types/dre';

/**
 * SPRINT 2: Botão Unificado "Ver Edições"
 * 
 * Componente que mostra um botão com contador de edições
 * e abre um modal detalhado com todas as alterações feitas na DRE
 * 
 * 🐛 FIX: Usa editCount do hook useDREIntegration para consistência
 * com ContextPanel (resolverá divergência 35 vs 90)
 */
export const UnifiedEditButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [filterCompany, setFilterCompany] = useState<'all' | 'Sétima' | '555 Studios'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const { dreData, masterData } = useDREStore();
  
  // 🐛 FIX: Usar editCount do hook para consistência
  const { editCount: hookEditCount } = useDREIntegration();

  // 🐛 FIX: Usar editCount do hook (fonte única de verdade)
  const editCount = hookEditCount;

  // 🐛 FIX: Usar mesma lista de campos editáveis do hook useDREIntegration
  // para garantir consistência entre editCount e editList.length
  const EDITABLE_FIELDS: (keyof DREYear)[] = [
    'receitaBruta',
    'impostosVendas',
    'custosDirectos',
    'despesasOperacionais',
    'depreciacao',
    'impostosLucro',
  ];

  // Gera lista de edições detalhadas (apenas campos editáveis)
  const getEditList = () => {
    if (!dreData || !masterData) return [];

    const edits: Array<{
      company: string;
      year: number;
      field: string;
      oldValue: number;
      newValue: number;
      difference: number;
    }> = [];

    // Coleta edições da Sétima (apenas campos editáveis)
    if (dreData.setima?.years && masterData.setima?.years) {
      dreData.setima.years.forEach((year, index) => {
        const masterYear = masterData.setima.years[index];
        if (!masterYear) return;

        // 🐛 FIX: Iterar apenas sobre campos editáveis (não todos)
        EDITABLE_FIELDS.forEach((key) => {
          const currentValue = year[key];
          const masterValue = masterYear[key];
          
          if (currentValue !== masterValue) {
            edits.push({
              company: 'Sétima',
              year: year.year,
              field: String(key),
              oldValue: Number(masterValue),
              newValue: Number(currentValue),
              difference: Number(currentValue) - Number(masterValue),
            });
          }
        });
      });
    }

    // Coleta edições do 555 Studios (apenas campos editáveis)
    if (dreData.studio555?.years && masterData.studio555?.years) {
      dreData.studio555.years.forEach((year, index) => {
        const masterYear = masterData.studio555.years[index];
        if (!masterYear) return;

        // 🐛 FIX: Iterar apenas sobre campos editáveis (não todos)
        EDITABLE_FIELDS.forEach((key) => {
          const currentValue = year[key];
          const masterValue = masterYear[key];
          
          if (currentValue !== masterValue) {
            edits.push({
              company: '555 Studios',
              year: year.year,
              field: String(key),
              oldValue: Number(masterValue),
              newValue: Number(currentValue),
              difference: Number(currentValue) - Number(masterValue),
            });
          }
        });
      });
    }

    return edits;
  };

  // editCount já definido acima usando hookEditCount
  const editList = getEditList();

  // Filtra edições com base nos filtros ativos
  const filteredEdits = useMemo(() => {
    let filtered = editList;

    // Filtro por empresa
    if (filterCompany !== 'all') {
      filtered = filtered.filter(edit => edit.company === filterCompany);
    }

    // Filtro por busca
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(edit =>
        edit.field.toLowerCase().includes(term) ||
        edit.year.toString().includes(term)
      );
    }

    return filtered;
  }, [editList, filterCompany, searchTerm]);

  const handleOpen = () => {
    setOpen(true);
    setFilterCompany('all');
    setSearchTerm('');
  };
  
  const handleClose = () => setOpen(false);

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<Visibility />}
        onClick={handleOpen}
        fullWidth
        sx={{
          py: 1.5,
          fontSize: '1rem',
          fontWeight: 500,
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
          },
        }}
      >
        Ver Edições
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Edit color="primary" />
            <Typography variant="h6" component="span">
              Edições Realizadas na DRE
            </Typography>
            <Chip
              label={`${editCount} edições`}
              color="primary"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          {/* Controles de Filtro */}
          <Stack spacing={2} mb={3}>
            {/* Campo de Busca */}
            <TextField
              placeholder="Buscar por métrica ou ano..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />

            {/* Filtro por Empresa */}
            <Stack direction="row" spacing={1} alignItems="center">
              <FilterList fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                Filtrar por:
              </Typography>
              <ToggleButtonGroup
                value={filterCompany}
                exclusive
                onChange={(_, newValue) => {
                  if (newValue !== null) setFilterCompany(newValue);
                }}
                size="small"
              >
                <ToggleButton value="all">
                  Todas
                </ToggleButton>
                <ToggleButton value="Sétima">
                  Sétima
                </ToggleButton>
                <ToggleButton value="555 Studios">
                  555 Studios
                </ToggleButton>
              </ToggleButtonGroup>
              <Chip
                label={`${filteredEdits.length} de ${editList.length}`}
                size="small"
                color="default"
                variant="outlined"
              />
            </Stack>
          </Stack>

          {editList.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="text.secondary">
                Nenhuma edição realizada ainda.
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                As alterações feitas na DRE aparecerão aqui.
              </Typography>
            </Box>
          ) : filteredEdits.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="text.secondary">
                Nenhuma edição encontrada com os filtros aplicados.
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                Tente ajustar os filtros ou limpar a busca.
              </Typography>
            </Box>
          ) : (
            <List>
              {filteredEdits.map((edit, index) => (
                <React.Fragment key={index}>
                  <ListItem
                    sx={{
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      py: 2,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <Chip
                        label={edit.company}
                        size="small"
                        color={edit.company === 'Sétima' ? 'secondary' : 'info'}
                        sx={{ fontWeight: 600 }}
                      />
                      <Typography variant="body2" fontWeight={600}>
                        {edit.year} - {edit.field}
                      </Typography>
                    </Stack>

                    <ListItemText
                      primary={
                        <Stack spacing={0.5}>
                          <Typography variant="body2" color="text.secondary">
                            Valor Original: <strong>{edit.oldValue.toLocaleString('pt-BR')}</strong>
                          </Typography>
                          <Typography variant="body2" color="primary.main" fontWeight={600}>
                            Novo Valor: <strong>{edit.newValue.toLocaleString('pt-BR')}</strong>
                          </Typography>
                          <Typography
                            variant="body2"
                            color={edit.difference >= 0 ? 'success.main' : 'error.main'}
                            fontWeight={600}
                          >
                            Diferença: {edit.difference >= 0 ? '+' : ''}
                            {edit.difference.toLocaleString('pt-BR')}
                            {' '}(
                            {edit.oldValue === 0 
                              ? 'N/A - base zero' 
                              : `${edit.difference >= 0 ? '+' : ''}${((edit.difference / edit.oldValue) * 100).toFixed(2)}%`
                            })
                          </Typography>
                        </Stack>
                      }
                    />
                  </ListItem>
                  {index < editList.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} variant="contained">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
